import { useEffect, useRef } from 'react';
import { matchPath, useLocation } from 'react-router-dom';

import { Bus } from '@atlas/core';

/**
 * @typedef {Object} Location
 * @property {string} pathname Current path of the location
 * @property {string} search Search parameters of the location
 */

/**
 * This callback is displayed as part of the Requester class.
 *
 * @callback ActionHandler
 * @param {Location} from Previous location
 * @param {Location} to Current location
 * @param {Object} params Path match params
 * @returns {void}
 */

/**
 * Matches the current path against a given mask, considering search parameters.
 *
 * @param {Location} currentLocation The current path
 * @param {string} mask Route mask to match the path with
 * @returns {Object|null} Returns an object of parameters if matched, otherwise null
 */
const matchPathWithMask = (currentLocation, mask) => {
  // If the mask starts with ?, we should match only search parameters
  if (mask.startsWith('?')) {
    const currentSearchParams = new URLSearchParams(currentLocation.search);
    const searchParamKey = mask.slice(1); // Remove '?' to get the key

    // Match the specific search parameter
    return currentSearchParams.has(searchParamKey)
      ? { [searchParamKey]: currentSearchParams.get(searchParamKey) }
      : null;
  }

  const matchResult = matchPath(currentLocation.pathname, { path: mask });

  if (matchResult) {
    return matchResult.params;
  }

  return null;
};

/**
 * Computes the added and removed search parameters between two locations.
 *
 * @param {Location} from Previous location
 * @param {Location} to Current location
 * @returns {Object} Returns an object containing added and removed search parameters
 */
export const calculateSearchParamChanges = (from, to) => {
  const prevParams = new URLSearchParams(from.search);
  const currentParams = new URLSearchParams(to.search);

  const added = [];
  const removed = [];

  const prevKeys = new Set(prevParams.keys());
  const currentKeys = new Set(currentParams.keys());

  for (const key of currentKeys) {
    if (!prevKeys.has(key)) added.push(key);
  }

  for (const key of prevKeys) {
    if (!currentKeys.has(key)) removed.push(key);
  }

  return { added, removed };
};

/**
 * @typedef PageActionConfig
 * @type {object}
 * @property {String} mask The mask for matching a route path
 * @property {ActionHandler} actionHandler Handler for navigation
 * @property {bool} ensureContentLoaded Enable mode for awaiting first content load
 */

/**
 * Special global hook to handle custom actions on route changes.
 *
 * @param {Array.<PageActionConfig>} config The configuration object for the routes
 */
export const useActionOnNavigation = config => {
  const location = useLocation();
  const previousLocationRef = useRef(undefined);

  useEffect(() => {
    const previousLocation = previousLocationRef.current;

    if (
      previousLocation &&
      previousLocation.pathname === location.pathname &&
      previousLocation.search === location.search
    ) {
      return;
    }

    for (const routeConfig of config) {
      const { actionHandler, ensureContentLoaded, mask } = routeConfig;

      // New location parameters
      const params = matchPathWithMask(location, mask);

      // For page configuration that matches
      if (params && typeof actionHandler === 'function') {
        if (ensureContentLoaded) {
          // Cleaning previous untriggered handlers
          if (Bus.listenerCount('ui:contentLoaded') > 0) {
            Bus.removeAllListeners();
          }
          // Attach action handler to content loaded event
          Bus.once(
            'ui:contentLoaded',
            actionHandler.bind(routeConfig, previousLocation, location, params),
          );
        } else {
          // Synchronically run action handler
          actionHandler(previousLocation, location, params);
        }

        // Stop processing further configurations after the first match
        break;
      }
    }

    previousLocationRef.current = location; // Update previous location
  }, [location, config]);
};

export const useContentLoadedNotification = () => {
  useEffect(() => {
    Bus.emit('ui:contentLoaded');
  }, []);
};
